import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { eventsAPI } from '../api';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import { Search, Camera, Ban, MessageCircle, Send, ShieldCheck, X, QrCode, BarChart3, Image, Users, User, Clock, Calendar, Facebook, Linkedin, Instagram, Share2, Upload, FileArchive } from 'lucide-react';
import Activities from '../components/Activities';
import NativeCameraUpload from '../components/NativeCameraUpload';
import UnifiedUpload from '../components/UnifiedUpload';
import { QRCodeSVG } from 'qrcode.react';
import './EventPhotos.css';

// Helper function to generate thumbnail URL
// For B2 photos, use thumbnail_url if available, otherwise fall back to full URL
// Cloudinary URLs get transformation parameters
const getCloudinaryThumbnail = (url, width = 400, height = 400, thumbnailUrl = null) => {
  if (!url) return url;
  
  // B2 URLs - use thumbnail_url if available (pre-generated thumbnail)
  if (url.includes('backblazeb2.com')) {
    return thumbnailUrl || url; // Use thumbnail if available, otherwise full URL
  }
  
  // Cloudinary URLs - add transformation parameters
  if (url.includes('cloudinary.com')) {
    return url.replace(
      '/upload/',
      `/upload/w_${width},h_${height},c_fill,q_auto,f_auto/`
    );
  }
  
  return url;
};

function EventPhotos() {
  const [photos, setPhotos] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [activeSlideshowPhotos, setActiveSlideshowPhotos] = useState([]); // Stable array for active slideshow
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSlideshowModal, setShowSlideshowModal] = useState(false);
  const [slideshowUsers, setSlideshowUsers] = useState([]); // Separate state for slideshow modal
  const [selectedSlideshowMusic, setSelectedSlideshowMusic] = useState('none');
  const [slideshowAudio, setSlideshowAudio] = useState(null);

  // Music options for slideshow
  const musicOptions = [
    { id: 'none', name: 'No Music', url: null },
    { id: 'upbeat', name: 'Upbeat Pop', url: 'https://www.bensound.com/bensound-music/bensound-ukulele.mp3' },
    { id: 'chill', name: 'Chill Electronic', url: 'https://www.bensound.com/bensound-music/bensound-creativeminds.mp3' },
    { id: 'acoustic', name: 'Acoustic Folk', url: 'https://www.bensound.com/bensound-music/bensound-sunny.mp3' },
    { id: 'cinematic', name: 'Cinematic', url: 'https://www.bensound.com/bensound-music/bensound-epic.mp3' },
    { id: 'jazz', name: 'Smooth Jazz', url: 'https://www.bensound.com/bensound-music/bensound-jazzyfrenchy.mp3' }
  ];
  const [tagType, setTagType] = useState('all'); // 'all', 'taken_by', 'appears_in'
  const [users, setUsers] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]); // For bulk selection
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagModalUsers, setTagModalUsers] = useState([]);
  const [isTagging, setIsTagging] = useState(false);

  const [activePhotoOverlay, setActivePhotoOverlay] = useState(null); // Mobile hamburger menu
  const [showPhotographersModal, setShowPhotographersModal] = useState(false);
  const [photographerSearch, setPhotographerSearch] = useState('');
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [attendeeSearch, setAttendeeSearch] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null); // null = broadcast, or photographer object
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showRegistrationQR, setShowRegistrationQR] = useState(false);
  const [showCameraQR, setShowCameraQR] = useState(false);
  const [showWebCameraQR, setShowWebCameraQR] = useState(false);
  const [showQRSelectionModal, setShowQRSelectionModal] = useState(false);
  const [thankYouStatus, setThankYouStatus] = useState({ enabled: false, sent: false, attendeeCount: 0 });
  const [sendingThankYou, setSendingThankYou] = useState(false);
  const [sendingFullGallery, setSendingFullGallery] = useState(false);
  const [showSendGalleryModal, setShowSendGalleryModal] = useState(false);
  const [galleryAttendees, setGalleryAttendees] = useState([]);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [selectAllAttendees, setSelectAllAttendees] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editEventName, setEditEventName] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [savingEvent, setSavingEvent] = useState(false);
  const [watermarkFile, setWatermarkFile] = useState(null);
  const [watermarkPreview, setWatermarkPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos', 'insights', or 'activities'
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('all'); // 'all' or activity ID
  const [showActivityAssignModal, setShowActivityAssignModal] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [showZipUpload, setShowZipUpload] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [zipUploadProgress, setZipUploadProgress] = useState({ current: 0, total: 0 });
  const [assigningActivity, setAssigningActivity] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const { eventId} = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  const normalizeTimestamp = (value) => {
    if (value == null) return Date.now();
    let n = Number(value);
    if (!Number.isFinite(n)) {
      n = Date.parse(value);
    }
    if (!Number.isFinite(n)) n = Date.now();
    if (n < 1e12) n *= 1000;
    return n;
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await eventsAPI.getPhotographerRequests(eventId);
      setPendingRequests(response.data?.requests || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchEventAndPhotos = async (signal) => {
    try {
      const cacheBust = { params: { t: Date.now() }, signal };
      
      const photographersRes = await eventsAPI.getPhotographers(eventId, 'all').catch(() => ({ data: { photographers: [] } }));
      
      const photographersList = photographersRes.data?.photographers || [];
      setUsers(photographersList);
      
      // Also fetch pending requests
      await fetchPendingRequests();
      
      // Fetch thank you SMS status
      try {
        const statusRes = await eventsAPI.getThankYouStatus(eventId);
        setThankYouStatus(statusRes.data);
      } catch (err) {
        console.error('Failed to fetch thank you status:', err);
      }
      
      const photographerIds = selectedUsers.length > 0 
        ? photographersList.filter(p => selectedUsers.includes(p.name)).map(p => p.id)
        : [];

      const [eventRes, photosRes, activitiesRes] = await Promise.all([
        eventsAPI.getOne(eventId, cacheBust),
        eventsAPI.getPhotos(eventId, { 
          ...cacheBust, 
          // Only send photographerIds for 'appears_in' mode (backend handles it)
          // For 'taken_by', we filter client-side
          photographerIds: (tagType === 'appears_in' && photographerIds.length > 0) ? photographerIds : undefined,
          tagType: (tagType === 'appears_in' && photographerIds.length > 0) ? tagType : undefined,
          // Filter by selected activity
          activityId: selectedActivity && selectedActivity !== 'all' ? selectedActivity : undefined
        }),
        eventsAPI.getActivities(eventId).catch(() => ({ data: { activities: [] } }))
      ]);

      const eventData = eventRes?.data?.event ?? null;
      setEvent(eventData);
      
      // Initialize edit form
      if (eventData) {
        setEditEventName(eventData.name || '');
        setEditStartTime(eventData.start_time ? new Date(parseInt(eventData.start_time)).toISOString().slice(0, 16) : '');
        setEditEndTime(eventData.end_time ? new Date(parseInt(eventData.end_time)).toISOString().slice(0, 16) : '');
      }

      const transformed = (photosRes?.data?.photos ?? []).map((p) => {
        const uploadedRaw = p.uploadedat ?? p.uploadedAt ?? p.createdAt;
        const uploadedAt = normalizeTimestamp(uploadedRaw);
        return { ...p, uploadedAt };
      });

      transformed.sort((a, b) => b.uploadedAt - a.uploadedAt);

      setPhotos(transformed);
      setActivities(activitiesRes?.data?.activities || []);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.message !== 'canceled') {
        console.error('Error fetching data:', err);
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchInsights = async (silent = false) => {
    try {
      if (!silent) setInsightsLoading(true);
      const response = await fetch(`https://live-event-photos.onrender.com/api/events/${eventId}/insights`);
      const data = await response.json();
      setInsights(data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      if (!silent) setInsightsLoading(false);
    }
  };

  const uniqueUsers = users.map(p => p.name).sort();
  
  // Apply filtering based on mode and selected users
  const filteredPhotos = (() => {
    let result = photos;
    
    // Filter by activity if selected
    if (selectedActivity !== 'all') {
      result = result.filter(photo => photo.activity_id === selectedActivity);
    }
    
    // If no users selected, return activity-filtered photos
    if (selectedUsers.length === 0) return result;
    
    // For "Taken By" mode, filter client-side by photographer name
    if (tagType === 'taken_by') {
      return result.filter(photo => selectedUsers.includes(photo.photographer));
    }
    
    // For "Appears In" mode, backend already filtered, so return all
    // (backend API handles this filtering)
    return result;
  })();
  
  // Compute slideshow photos based on modal selection
  const slideshowPhotos = (() => {
    if (slideshowUsers.length === 0) return filteredPhotos;
    return filteredPhotos.filter(photo => slideshowUsers.includes(photo.photographer));
  })();

  useEffect(() => {
    let timer;
    let socket;
    const controller = new AbortController();

    // Initial fetch
    const poll = async () => {
      await fetchEventAndPhotos(controller.signal);
      timer = setTimeout(poll, 60000); // Fallback polling every 60 seconds (only if WebSocket fails)
    };

    if (initialLoading) {
      setLoading(true);
    }
    poll();

    // Set up WebSocket connection for real-time updates
    const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com';
    socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => {
      console.log('🔌 WebSocket connected');
      socket.emit('join-event', eventId);
      // Clear fallback polling when WebSocket connects
      clearTimeout(timer);
    });

    socket.on('photo-uploaded', (photoData) => {
      console.log('📸 New photo received via WebSocket:', photoData);
      // Refresh photos to get the new one
      fetchEventAndPhotos(controller.signal);
    });

    socket.on('disconnect', () => {
      console.log('🔌 WebSocket disconnected, falling back to polling');
      // Resume polling if WebSocket disconnects
      timer = setTimeout(poll, 15000);
    });

    return () => {
      clearTimeout(timer);
      controller.abort();
      if (socket) {
        socket.emit('leave-event', eventId);
        socket.disconnect();
      }
    };
  }, [eventId, tagType, selectedUsers, selectedActivity]);

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Restore active tab from localStorage
  useEffect(() => {
    if (eventId) {
      const savedTab = localStorage.getItem(`eventTab_${eventId}`);
      if (savedTab) setActiveTab(savedTab);
    }
  }, [eventId]);

  // Fetch insights when switching to insights tab and auto-refresh every 10s
  useEffect(() => {
    if (activeTab === 'insights') {
      if (!insights) fetchInsights();
      
      const interval = setInterval(() => {
        fetchInsights(true); // Silent refresh - no loading spinner
      }, 10000); // Refresh every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Update active slideshow with new photos when they arrive AND remove deleted photos
  useEffect(() => {
    if (!slideshowActive) return;
    
    const slideshowPhotoIds = new Set(slideshowPhotos.map(p => p.id));
    
    // Find new photos that aren't in the active slideshow yet
    const newPhotos = slideshowPhotos.filter(
      photo => !activeSlideshowPhotos.some(existing => existing.id === photo.id)
    );
    
    // Find deleted photos that are in the active slideshow but not in slideshowPhotos anymore
    const deletedPhotoIds = new Set(
      activeSlideshowPhotos
        .filter(photo => !slideshowPhotoIds.has(photo.id))
        .map(photo => photo.id)
    );
    
    const hasChanges = newPhotos.length > 0 || deletedPhotoIds.size > 0;
    
    if (hasChanges) {
      setActiveSlideshowPhotos(prev => {
        // Remove deleted photos
        let updated = prev.filter(photo => !deletedPhotoIds.has(photo.id));
        
        // If we deleted the current photo, adjust index
        if (deletedPhotoIds.has(prev[currentSlideIndex]?.id)) {
          // Move to next photo or stay at same index if it's now a different photo
          setCurrentSlideIndex(prevIndex => {
            if (updated.length === 0) return 0;
            // Keep the same index, which will now show the next photo
            return Math.min(prevIndex, updated.length - 1);
          });
        } else if (deletedPhotoIds.size > 0) {
          // Adjust current index if photos before it were deleted
          const deletedBeforeCurrent = prev
            .slice(0, currentSlideIndex)
            .filter(photo => deletedPhotoIds.has(photo.id)).length;
          if (deletedBeforeCurrent > 0) {
            setCurrentSlideIndex(prevIndex => Math.max(0, prevIndex - deletedBeforeCurrent));
          }
        }
        
        // Add new photos after the current slide position
        if (newPhotos.length > 0) {
          const adjustedIndex = Math.min(currentSlideIndex, updated.length - 1);
          const before = updated.slice(0, adjustedIndex + 1);
          const after = updated.slice(adjustedIndex + 1);
          updated = [...before, ...newPhotos, ...after];
        }
        
        return updated;
      });
    }
  }, [slideshowActive, slideshowPhotos, currentSlideIndex, activeSlideshowPhotos]);

  useEffect(() => {
    if (!slideshowActive || activeSlideshowPhotos.length === 0) return;

    const timer = setInterval(() => {
      setCurrentSlideIndex((prev) => {
        const nextIndex = (prev + 1) % activeSlideshowPhotos.length;
        return nextIndex;
      });
    }, 4000);

    return () => clearInterval(timer);
  }, [slideshowActive, activeSlideshowPhotos]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && slideshowActive) {
        setSlideshowActive(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slideshowActive]);

  const openSlideshowModal = () => {
    setShowSlideshowModal(true);
  };

  const startSlideshow = () => {
    if (slideshowPhotos.length > 0) {
      setShowSlideshowModal(false);
      setActiveSlideshowPhotos(slideshowPhotos); // Initialize with current filtered photos
      setCurrentSlideIndex(0);
      setSlideshowActive(true);
      
      // Start playing selected music
      const selectedOption = musicOptions.find(opt => opt.id === selectedSlideshowMusic);
      if (selectedOption && selectedOption.url) {
        const audio = new Audio(selectedOption.url);
        audio.loop = true;
        audio.play();
        setSlideshowAudio(audio);
      }
      
      document.documentElement.requestFullscreen?.() || 
      document.documentElement.webkitRequestFullscreen?.();
    }
  };

  const stopSlideshow = () => {
    setSlideshowActive(false);
    
    // Stop music
    if (slideshowAudio) {
      slideshowAudio.pause();
      slideshowAudio.currentTime = 0;
      setSlideshowAudio(null);
    }
    
    document.exitFullscreen?.() || 
    document.webkitExitFullscreen?.();
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await eventsAPI.deletePhoto(eventId, photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleCameraCapture = async (file) => {
    const formData = new FormData();
    formData.append('photos', file);
    
    // Upload the photo
    await eventsAPI.uploadPhotos(eventId, formData);
    
    // Refresh photos list
    const controller = new AbortController();
    await fetchEventAndPhotos(controller.signal);
  };

  const handleUnblockPhotographer = async (photographerId) => {
    if (!window.confirm('Unblock this photographer? They will be able to upload photos again.')) {
      return;
    }

    try {
      console.log('Attempting to unblock photographer:', photographerId);
      const response = await eventsAPI.unblockPhotographer(photographerId);
      console.log('Unblock response:', response);
      // Refresh photographers list
      const photographersRes = await eventsAPI.getPhotographers(eventId, 'all');
      setUsers(photographersRes.data?.photographers || []);
      alert('Photographer unblocked successfully!');
    } catch (err) {
      console.error('Error unblocking photographer:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert(`Failed to unblock photographer: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleBlockPhotographer = async (photographerId) => {
    if (!window.confirm('Block this photographer? They will not be able to upload photos.')) {
      return;
    }

    try {
      await eventsAPI.blockPhotographer(photographerId);
      // Refresh photographers list
      const photographersRes = await eventsAPI.getPhotographers(eventId, 'all');
      setUsers(photographersRes.data?.photographers || []);
      alert('Photographer blocked successfully!');
    } catch (err) {
      console.error('Error blocking photographer:', err);
      alert(`Failed to block photographer: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleApprovePhotographer = async (photographerId) => {
    if (!window.confirm('Approve this attendee as a photographer? They will be able to upload photos.')) {
      return;
    }

    try {
      await eventsAPI.approvePhotographer(photographerId);
      alert('Photographer approved successfully!');
      await fetchEventAndPhotos();
    } catch (err) {
      console.error('Error approving photographer:', err);
      alert(`Failed to approve photographer: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleConvertToAttendee = async (photographerId) => {
    if (!window.confirm('Convert this photographer to an attendee? They will lose photo upload access.')) {
      return;
    }

    try {
      await eventsAPI.convertToAttendee(photographerId);
      alert('Converted to attendee successfully!');
      await fetchEventAndPhotos();
    } catch (err) {
      console.error('Error converting to attendee:', err);
      alert(`Failed to convert to attendee: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleConvertToPhotographer = async (attendeeId) => {
    if (!window.confirm('Convert this attendee to a photographer? They will be able to upload photos.')) {
      return;
    }

    try {
      await eventsAPI.convertToPhotographer(attendeeId);
      alert('Converted to photographer successfully!');
      await fetchEventAndPhotos();
    } catch (err) {
      console.error('Error converting to photographer:', err);
      alert(`Failed to convert to photographer: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      alert('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      if (messageRecipient?.name) {
        // Send to individual
        await eventsAPI.sendNotification(messageRecipient.id, messageText);
        alert(`Message sent to ${messageRecipient.name}!`);
      } else if (messageRecipient?.role) {
        // Broadcast to specific role
        await eventsAPI.broadcastNotification(eventId, messageText, messageRecipient.role);
        const roleName = messageRecipient.role === 'attendee' ? 'attendees' : 'photographers';
        alert(`Message sent to all ${roleName}!`);
      } else {
        // Broadcast to all
        await eventsAPI.broadcastNotification(eventId, messageText, 'all');
        alert('Message sent to everyone!');
      }
      setShowMessageModal(false);
      setMessageText('');
      setMessageRecipient(null);
    } catch (err) {
      console.error('Error sending message:', err);
      alert(`Failed to send message: ${err.response?.data?.error || err.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === uniqueUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([...uniqueUsers]);
    }
  };

  const handleSelectAllPhotos = () => {
    if (selectedPhotos.length === filteredPhotos.length) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(filteredPhotos.map(p => p.id));
    }
  };

  const handleTogglePhotoSelection = (photoId) => {
    if (selectedPhotos.includes(photoId)) {
      setSelectedPhotos(selectedPhotos.filter(id => id !== photoId));
    } else {
      setSelectedPhotos([...selectedPhotos, photoId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedPhotos.length} photo(s)?`)) {
      return;
    }

    try {
      await Promise.all(selectedPhotos.map(photoId => 
        eventsAPI.deletePhoto(eventId, photoId)
      ));
      setPhotos(photos.filter(p => !selectedPhotos.includes(p.id)));
      setSelectedPhotos([]);
      alert(`Successfully deleted ${selectedPhotos.length} photo(s)`);
    } catch (err) {
      console.error('Error deleting photos:', err);
      alert('Failed to delete some photos. Please try again.');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedPhotos.length === 0) return;

    try {
      // Import JSZip dynamically
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Download all selected photos
      const downloadPromises = selectedPhotos.map(async (photoId) => {
        const photo = photos.find(p => p.id === photoId);
        if (!photo) return;

        try {
          const response = await fetch(photo.url);
          const blob = await response.blob();
          const filename = `photo_${photoId}.jpg`;
          zip.file(filename, blob);
        } catch (err) {
          console.error(`Failed to download photo ${photoId}:`, err);
        }
      });

      await Promise.all(downloadPromises);

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event?.name || 'event'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Successfully downloaded ${selectedPhotos.length} photo(s)`);
    } catch (err) {
      console.error('Error downloading photos:', err);
      alert('Failed to download photos. Please try again.');
    }
  };

  const handleOpenTagModal = () => {
    if (selectedPhotos.length === 0) {
      alert('Please select at least one photo to tag');
      return;
    }
    setShowTagModal(true);
  };

  const handleBulkTag = async () => {
    if (selectedPhotos.length === 0 || tagModalUsers.length === 0) {
      alert('Please select photos and users to tag');
      return;
    }

    setIsTagging(true);
    try {
      const userIds = users.filter(u => tagModalUsers.includes(u.name)).map(u => u.id);
      
      await eventsAPI.tagPhotos(eventId, {
        photoIds: selectedPhotos,
        photographerIds: userIds,
        tagType: 'appears_in'
      });
      
      // Success - close modal and reset
      setShowTagModal(false);
      setTagModalUsers([]);
      setSelectedPhotos([]);
      alert(`Successfully tagged ${selectedPhotos.length} photo(s) with ${tagModalUsers.length} user(s)`);
    } catch (err) {
      console.error('Error tagging photos:', err);
      alert('Failed to tag photos. Please try again.');
    } finally {
      setIsTagging(false);
    }
  };

  const handleBulkAssignActivity = async () => {
    if (selectedPhotos.length === 0) {
      alert('Please select photos to assign');
      return;
    }

    try {
      setAssigningActivity(true);
      const activityId = selectedActivity === 'all' ? null : selectedActivity;
      
      await eventsAPI.assignPhotosToActivity(eventId, {
        photoIds: selectedPhotos,
        activityId: activityId
      });
      
      // Update local photos state
      setPhotos(photos.map(photo => 
        selectedPhotos.includes(photo.id) 
          ? { ...photo, activity_id: activityId }
          : photo
      ));
      
      setShowActivityAssignModal(false);
      setSelectedPhotos([]);
      const activityName = activityId ? activities.find(a => a.id === activityId)?.name : 'No Activity';
      alert(`Successfully assigned ${selectedPhotos.length} photo(s) to ${activityName}`);
    } catch (err) {
      console.error('Error assigning photos to activity:', err);
      alert('Failed to assign photos. Please try again.');
    } finally {
      setAssigningActivity(false);
    }
  };

  // Don't hide Sidebar during loading - show loading state within page content
  // if (loading && initialLoading) {
  //   return (
  //     <div className="loading-screen">
  //       <div className="loading-content">
  //         <div className="spinner"></div>
  //         <p>Loading photos...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Check if user is super admin
  const getUserData = () => {
    const stored = localStorage.getItem('user');
    console.log('📦 EventPhotos - localStorage user:', stored);
    if (!stored || stored === 'undefined') return null;
    try {
      const parsed = JSON.parse(stored);
      console.log('📦 EventPhotos - parsed user data:', parsed);
      return parsed;
    } catch (e) {
      console.error('📦 EventPhotos - failed to parse user:', e);
      return null;
    }
  };
  const userData = getUserData();
  const isSuperAdmin = userData?.role === 'super_admin';
  console.log('🔐 EventPhotos - isSuperAdmin:', isSuperAdmin, 'userData.role:', userData?.role);

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />

      <Sidebar onLogout={() => { localStorage.clear(); navigate('/'); }} isSuperAdmin={isSuperAdmin} />
      
      {/* Header */}
      <header className="photos-header">
        <div className="header-right">
          <button 
            onClick={() => setShowCameraCapture(true)} 
            className="header-button" 
            style={{
              backgroundColor: '#EC4899',
              display: window.innerWidth <= 768 ? 'inline-flex' : 'none'
            }}
          >
            <Camera size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
            Take Photo
          </button>
          <button onClick={() => setShowZipUpload(true)} className="header-button" style={{backgroundColor: '#F97316'}}>
            <Upload size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
            Upload
          </button>
          <button onClick={() => setShowEditEventModal(true)} className="header-button" style={{backgroundColor: '#3B82F6'}}>
            <Calendar size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
            Edit Event
          </button>
          <button onClick={() => navigate(`/events/${eventId}/users`)} className="header-button" style={{backgroundColor: '#8B5CF6'}}>
            <Users size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
            Manage Users
          </button>
          <button onClick={() => setShowQRSelectionModal(true)} className="header-button" style={{backgroundColor: '#10B981'}}>
            <QrCode size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
            QR Codes
          </button>
          {thankYouStatus.enabled && thankYouStatus.attendeeCount > 0 && (
            <button 
              onClick={async () => {
                if (thankYouStatus.sent) {
                  alert('Thank you SMS has already been sent for this event.');
                  return;
                }
                if (!window.confirm(`Send gallery links to ${thankYouStatus.attendeeCount} attendees?`)) {
                  return;
                }
                try {
                  setSendingThankYou(true);
                  const response = await eventsAPI.sendThankYouSms(eventId);
                  alert(`Successfully sent gallery links to ${response.data.totalAttendees} attendees!`);
                  // Refresh status
                  const statusRes = await eventsAPI.getThankYouStatus(eventId);
                  setThankYouStatus(statusRes.data);
                } catch (err) {
                  console.error('Failed to send thank you SMS:', err);
                  alert(`Failed to send gallery links: ${err.response?.data?.error || err.message}`);
                } finally {
                  setSendingThankYou(false);
                }
              }}
              className="header-button" 
              style={{backgroundColor: thankYouStatus.sent ? '#9CA3AF' : '#F59E0B', cursor: thankYouStatus.sent ? 'not-allowed' : 'pointer'}}
              disabled={sendingThankYou || thankYouStatus.sent}
            >
              <Send size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
              {sendingThankYou ? 'Sending...' : thankYouStatus.sent ? 'Gallery Links Sent' : 'Send Gallery Links'}
            </button>
          )}
          {thankYouStatus.attendeeCount > 0 && (
            <button 
              onClick={async () => {
                // Open modal to select attendees
                try {
                  const response = await eventsAPI.getUsers(eventId, 'attendee');
                  setGalleryAttendees(response.data.users || []);
                  setSelectedAttendees([]);
                  setSelectAllAttendees(false);
                  setShowSendGalleryModal(true);
                } catch (err) {
                  console.error('Failed to load attendees:', err);
                  alert('Failed to load attendees');
                }
              }}
              className="header-button" 
              style={{backgroundColor: '#10B981', cursor: sendingFullGallery ? 'wait' : 'pointer'}}
              disabled={sendingFullGallery}
            >
              <Share2 size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
              {sendingFullGallery ? 'Sending...' : 'Send Full Gallery'}
            </button>
          )}
          {filteredPhotos.length > 0 && (
            <button onClick={openSlideshowModal} className="header-button" style={{backgroundColor: '#4F46E5'}}>
              <Image size={18} style={{marginRight: '8px', verticalAlign: 'middle'}} />
              Start Slideshow
            </button>
          )}
        </div>
      </header>
      
      {/* Event Info Bar */}
      <div className="event-info-bar desktop-only">
        <p>{event?.name} • {filteredPhotos.length} photos</p>
      </div>

      {/* Main Tab Navigation */}
      <div className="main-tabs-container">
        <button
          onClick={() => {
            setActiveTab('photos');
            localStorage.setItem(`eventTab_${eventId}`, 'photos');
          }}
          className={`main-tab ${activeTab === 'photos' ? 'active' : ''}`}
        >
          <Camera size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
          Photos
        </button>
        <button
          onClick={() => {
            setActiveTab('insights');
            localStorage.setItem(`eventTab_${eventId}`, 'insights');
          }}
          className={`main-tab ${activeTab === 'insights' ? 'active' : ''}`}
        >
          <BarChart3 size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
          Insights
        </button>
        <button
          onClick={() => {
            setActiveTab('activities');
            localStorage.setItem(`eventTab_${eventId}`, 'activities');
          }}
          className={`main-tab ${activeTab === 'activities' ? 'active' : ''}`}
        >
          <Calendar size={18} style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
          Activities
        </button>
      </div>

      <div className="photos-content">
        {activeTab === 'photos' ? (
          <>
        {/* Filter Container */}
        <div className="filter-container desktop-only">
          {/* Filter Tabs */}
          <div className="filter-tabs">
            <button
              onClick={() => setTagType('all')}
              className={`filter-tab ${tagType === 'all' ? 'active' : ''}`}
            >
              <Image size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} />
              All Photos
            </button>
            <button
              onClick={() => setTagType('taken_by')}
              className={`filter-tab ${tagType === 'taken_by' ? 'active' : ''}`}
            >
              <Camera size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} />
              Taken By
            </button>
            <button
              onClick={() => setTagType('appears_in')}
              className={`filter-tab ${tagType === 'appears_in' ? 'active' : ''}`}
            >
              <User size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} />
              Appears In
            </button>
          </div>

          {/* Activity Filter */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} style={{ color: '#8B5CF6' }} />
            <select 
              value={selectedActivity} 
              onChange={(e) => setSelectedActivity(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #374151',
                backgroundColor: '#1F2937',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <option value="all">All Activities</option>
              {activities.map(activity => (
                <option key={activity.id} value={activity.id}>
                  {activity.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          {(tagType === 'taken_by' || tagType === 'appears_in') && (
          <div className="search-container">
            <span className="search-icon"><Search size={18} /></span>
            <input
              type="text"
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setDropdownOpen(true)}
              className="search-input"
            />
            
            {dropdownOpen && (
              <>
                <div 
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                  onClick={() => setDropdownOpen(false)}
                />
                <div className="dropdown-menu">
                  {uniqueUsers
                    .filter(name => name.toLowerCase().includes(searchInput.toLowerCase()))
                    .map(name => (
                      <div
                        key={name}
                        onClick={() => {
                          if (selectedUsers.includes(name)) {
                            setSelectedUsers(selectedUsers.filter(p => p !== name));
                          } else {
                            setSelectedUsers([...selectedUsers, name]);
                          }
                        }}
                        className={`dropdown-item ${selectedUsers.includes(name) ? 'selected' : ''}`}
                      >
                        <span>{name}</span>
                        {selectedUsers.includes(name) && <span>✓</span>}
                      </div>
                    ))}
                </div>
              </>
            )}
            
            {selectedUsers.length > 0 && (
              <div className="selected-tags">
                {selectedUsers.map(name => (
                  <span key={name} className="tag-chip">
                    {name}
                    <button
                      onClick={() => setSelectedUsers(selectedUsers.filter(p => p !== name))}
                      className="tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Select All Button */}
          {(tagType === 'taken_by' || tagType === 'appears_in') && (
          <button onClick={handleSelectAll} className="select-all-button">
            <span>☰</span>
            <span>{selectedUsers.length === uniqueUsers.length ? 'Deselect All' : 'Select All'}</span>
          </button>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {filteredPhotos.length > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-left">
              <button onClick={handleSelectAllPhotos} className="select-photos-button">
                {selectedPhotos.length === filteredPhotos.length ? '☑ Deselect All Photos' : '☐ Select All Photos'}
              </button>
              {selectedPhotos.length > 0 && (
                <span className="selection-count">{selectedPhotos.length} photo(s) selected</span>
              )}
            </div>
            {selectedPhotos.length > 0 && (
              <div className="bulk-actions-right">
                <button onClick={() => setShowActivityAssignModal(true)} className="tag-button" style={{ backgroundColor: '#8B5CF6' }}>
                  <Clock size={16} style={{ display: 'inline-block', marginRight: '6px', verticalAlign: 'middle' }} />
                  Assign to Activity
                </button>
                <button onClick={handleOpenTagModal} className="tag-button">
                  🏷️ Tag Selected
                </button>
                <button onClick={handleBulkDownload} className="download-button">
                  📥 Download Selected
                </button>
                <button onClick={handleBulkDelete} className="bulk-delete-button">
                  🗑️ Delete Selected
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photos Grid */}
        {loading && initialLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading photos...</p>
          </div>
        ) : filteredPhotos.length === 0 ? (
          <div className="empty-state">
            {selectedUsers.length > 0 ? (
              <>
                <p>No photos found for selected users</p>
                <p>Try a different search term or clear the filter.</p>
              </>
            ) : (
              <>
                <p>No photos uploaded yet for this event.</p>
                <p>Share the QR code with users to start collecting photos!</p>
              </>
            )}
          </div>
        ) : (
          <div className="photos-grid">
            {filteredPhotos.map((photo) => {
              // Use thumbnail for grid display (400x400)
              const thumbnailUrl = getCloudinaryThumbnail(photo.url, 400, 400, photo.thumbnail_url);
              const imgUrl = `${thumbnailUrl}${thumbnailUrl.includes('?') ? '&' : '?'}v=${photo.uploadedAt}`;
              const isSelected = selectedPhotos.includes(photo.id);
              return (
                <div key={photo.id} className={`photo-card ${isSelected ? 'selected' : ''}`}>
                  <div 
                    className={`photo-image-wrapper ${activePhotoOverlay === photo.id ? 'show-overlay' : ''}`}
                    onClick={() => {
                      // Toggle overlay on mobile
                      if (window.innerWidth <= 768) {
                        setActivePhotoOverlay(activePhotoOverlay === photo.id ? null : photo.id);
                      }
                    }}
                  >
                    <div className="photo-checkbox-container">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleTogglePhotoSelection(photo.id)}
                        className="photo-checkbox"
                      />
                    </div>
                    <img src={imgUrl} alt={photo.originalName} />
                    <div className="photo-overlay">
                      <div className="photo-overlay-content">
                        <p className="overlay-photographer">📷 {photo.photographer}</p>
                        <p className="overlay-timestamp">{new Date(photo.uploadedAt).toLocaleString()}</p>
                        {photo.tagged_users && photo.tagged_users.length > 0 && (
                          <p className="overlay-tagged">
                            <Users size={14} style={{display: 'inline', marginRight: '4px'}} /> Tagged: {photo.tagged_users.map(u => u.name).join(', ')}
                          </p>
                        )}
                        <div className="overlay-actions">
                          <a href={photo.url} download target="_blank" rel="noopener noreferrer" className="overlay-download-button">
                            ⬇️ Download
                          </a>
                          <button onClick={() => handleDeletePhoto(photo.id)} className="overlay-delete-button">
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
                     })}
          </div>
        )}
          </>
        ) : activeTab === 'insights' ? (
          /* Insights Tab Content */
          <div className="insights-tab-content">
            {insightsLoading ? (
              <div className="insights-loading">Loading insights...</div>
            ) : insights ? (
              <>
                {/* Key Metrics Grid */}
                <div className="insights-grid">
                  <div className="insight-card">
                    <div className="insight-label">Photos Taken</div>
                    <div className="insight-value">{insights.photosTaken || 0}</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Photos Delivered</div>
                    <div className="insight-value">{insights.photosDelivered || 0}</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Delivery Success Rate</div>
                    <div className="insight-value">{insights.deliverySuccessRate || 0}%</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Unique Attendees Reached</div>
                    <div className="insight-value">{insights.uniqueAttendeesReached || 0}</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Avg Photos (Engaged)</div>
                    <div className="insight-value">{insights.avgPhotosPerAttendee || 0}</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Avg Delivery Speed</div>
                    <div className="insight-value">
                      {insights.avgDeliverySpeedSeconds 
                        ? `${Math.floor(insights.avgDeliverySpeedSeconds / 60)}m ${insights.avgDeliverySpeedSeconds % 60}s`
                        : 'N/A'}
                    </div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Shares</div>
                    <div className="insight-value">{insights.totalShares || 0}</div>
                  </div>
                  <div className="insight-card">
                    <div className="insight-label">Auto-tag Accuracy</div>
                    <div className="insight-value">
                      {insights.autoTagAccuracyPercent != null && insights.autoTagAccuracyPercent !== undefined
                        ? `${insights.autoTagAccuracyPercent}%` 
                        : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Social Shares by Platform */}
                {insights.sharesByPlatform && insights.sharesByPlatform.length > 0 && (
                  <div className="insights-section">
                    <h3>Social Shares by Platform</h3>
                    <div className="shares-platform-grid">
                      {insights.sharesByPlatform.map((platform) => (
                        <div key={platform.platform} className="share-platform-card">
                          <div className="platform-name">
                            {platform.platform === 'facebook' && (
                              <><Facebook size={18} /> Facebook</>
                            )}
                            {platform.platform === 'linkedin' && (
                              <><Linkedin size={18} /> LinkedIn</>
                            )}
                            {platform.platform === 'instagram' && (
                              <><Instagram size={18} /> Instagram</>
                            )}
                            {platform.platform === 'native' && (
                              <><Share2 size={18} /> Native Share</>
                            )}
                          </div>
                          <div className="platform-count">{platform.count} shares</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Most Shared Photos */}
                {insights.mostSharedPhotos && insights.mostSharedPhotos.length > 0 && (
                  <div className="insights-section">
                    <h3>Most Shared Photos</h3>
                    <div className="most-shared-photos-grid">
                      {insights.mostSharedPhotos.map((photo) => (
                        <div key={photo.id} className="shared-photo-card">
                          <img 
                            src={photo.filename} 
                            alt="Shared photo"
                            className="shared-photo-thumb"
                          />
                          <div className="shared-photo-count">{photo.share_count} shares</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Tagged Attendees */}
                {insights.topTaggedAttendees && insights.topTaggedAttendees.length > 0 && (
                  <div className="insights-section">
                    <h3>Top 5 Most Tagged Attendees</h3>
                    <div className="top-attendees-list">
                      {insights.topTaggedAttendees.map((attendee, index) => (
                        <div key={attendee.id} className="top-attendee-item">
                          <span className="attendee-rank">#{index + 1}</span>
                          <span className="attendee-name">{attendee.name}</span>
                          <span className="attendee-count">{attendee.photo_count} photos</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="insights-error">Failed to load insights</div>
            )}
          </div>
        ) : activeTab === 'activities' ? (
          <Activities 
            eventId={eventId} 
            theme={theme} 
            onActivityClick={(activityId) => {
              setSelectedActivity(activityId);
              setActiveTab('photos');
              localStorage.setItem(`eventTab_${eventId}`, 'photos');
            }}
          />
        ) : null}
      </div>

      {/* Slideshow Modal */}
      {showSlideshowModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#1f2937' }}>Start Slideshow</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>The slideshow will display {slideshowPhotos.length} photos in fullscreen mode.</p>
            
            {/* User Filter */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#374151', fontWeight: '600', fontSize: '14px' }}>Filter by User (Optional)</label>
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto', 
                border: '2px solid #e5e7eb', 
                borderRadius: '8px',
                padding: '8px'
              }}>
                {uniqueUsers.map(name => {
                  const isSelected = slideshowUsers.includes(name);
                  return (
                    <div
                      key={name}
                      onClick={() => {
                        if (isSelected) {
                          setSlideshowUsers(slideshowUsers.filter(p => p !== name));
                        } else {
                          setSlideshowUsers([...slideshowUsers, name]);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : 'white',
                        borderRadius: '6px',
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = isSelected ? '#dbeafe' : '#f9fafb'}
                      onMouseLeave={(e) => e.target.style.background = isSelected ? '#eff6ff' : 'white'}
                    >
                      <span style={{ color: '#374151', fontSize: '14px' }}>{name}</span>
                      {isSelected && <span style={{ color: '#2563eb', fontWeight: 'bold' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
              {slideshowUsers.length > 0 && (
                <button
                  onClick={() => setSlideshowUsers([])}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#6b7280',
                    fontWeight: '600'
                  }}
                >
                  Clear Selection
                </button>
              )}
            </div>
            
            {/* Music Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#374151', fontWeight: '600', fontSize: '14px' }}>Slideshow Music</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {musicOptions.map(option => (
                  <div
                    key={option.id}
                    onClick={() => setSelectedSlideshowMusic(option.id)}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      background: selectedSlideshowMusic === option.id ? '#eff6ff' : 'white',
                      border: `2px solid ${selectedSlideshowMusic === option.id ? '#2563eb' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedSlideshowMusic !== option.id) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedSlideshowMusic !== option.id) {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>{option.name}</span>
                    {selectedSlideshowMusic === option.id && <span style={{ color: '#2563eb', fontWeight: 'bold' }}>✓</span>}
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={startSlideshow}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Start
              </button>
              <button
                onClick={() => setShowSlideshowModal(false)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#1f2937' }}>Tag {selectedPhotos.length} Photo(s)</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>Select users to tag in the selected photos.</p>
            
            {/* User Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#374151', fontWeight: '600', fontSize: '14px' }}>Select Users to Tag</label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '2px solid #e5e7eb', 
                borderRadius: '8px',
                padding: '8px'
              }}>
                {uniqueUsers.map(name => {
                  const isSelected = tagModalUsers.includes(name);
                  return (
                    <div
                      key={name}
                      onClick={() => {
                        if (isSelected) {
                          setTagModalUsers(tagModalUsers.filter(u => u !== name));
                        } else {
                          setTagModalUsers([...tagModalUsers, name]);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : 'white',
                        borderRadius: '6px',
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? '#dbeafe' : '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#eff6ff' : 'white'}
                    >
                      <span style={{ color: '#374151', fontSize: '14px' }}>{name}</span>
                      {isSelected && <span style={{ color: '#2563eb', fontWeight: 'bold' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
              {tagModalUsers.length > 0 && (
                <div style={{ marginTop: '12px', color: '#6b7280', fontSize: '14px' }}>
                  {tagModalUsers.length} user(s) selected
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleBulkTag}
                disabled={tagModalUsers.length === 0 || isTagging}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: (tagModalUsers.length === 0 || isTagging) ? '#d1d5db' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (tagModalUsers.length === 0 || isTagging) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isTagging && (
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                )}
                {isTagging ? 'Tagging...' : 'Tag Photos'}
              </button>
              <button
                onClick={() => {
                  setShowTagModal(false);
                  setTagModalUsers([]);
                }}
                disabled={isTagging}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: isTagging ? '#f3f4f6' : '#e5e7eb',
                  color: isTagging ? '#9ca3af' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isTagging ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Assignment Modal */}
      {showActivityAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: '32px',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#1f2937' }}>Assign to Activity</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>Assign {selectedPhotos.length} photo(s) to an activity.</p>
            
            {/* Activity Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#374151', fontWeight: '600', fontSize: '14px' }}>Select Activity</label>
              <select 
                value={selectedActivity === 'all' ? '' : selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value || 'all')}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                <option value="">No Activity (Remove assignment)</option>
                {activities.map(activity => (
                  <option key={activity.id} value={activity.id}>
                    {activity.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleBulkAssignActivity}
                disabled={assigningActivity}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: assigningActivity ? '#d1d5db' : '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: assigningActivity ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                {assigningActivity ? 'Assigning...' : 'Assign Activity'}
              </button>
              <button
                onClick={() => setShowActivityAssignModal(false)}
                disabled={assigningActivity}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: assigningActivity ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photographers Management Modal */}
      {showPhotographersModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#111827' }}>Manage Photographers</h2>
              <button
                onClick={() => {
                  setShowPhotographersModal(false);
                  setPhotographerSearch('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Broadcast Button */}
            <button
              onClick={() => {
                setMessageRecipient({ role: 'photographer' });
                setShowMessageModal(true);
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={16} />
              Broadcast to All Photographers
            </button>

            <input
              type="text"
              placeholder="Search photographers..."
              value={photographerSearch}
              onChange={(e) => setPhotographerSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            />

            <div style={{ marginBottom: '12px', color: '#6b7280', fontSize: '14px' }}>
              {users.filter(u => u.role === 'photographer').length} photographer(s) registered
            </div>

            <div>
              {users
                .filter(p => p.role === 'photographer' && p.name.toLowerCase().includes(photographerSearch.toLowerCase()))
                .map(photographer => (
                  <div
                    key={photographer.id}
                    style={{
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: photographer.is_blocked ? '#fef2f2' : 'white'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        {photographer.name}
                      </div>
                      {photographer.email && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {photographer.email}
                        </div>
                      )}
                      
                      {/* Strike Count - Always show */}
                      <div style={{ 
                        fontSize: '12px', 
                        color: photographer.is_blocked ? '#dc2626' : (photographer.rejection_count > 0 ? '#f59e0b' : '#10b981'),
                        marginBottom: '8px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {photographer.is_blocked ? (
                          <>
                            <Ban size={14} />
                            BLOCKED ({photographer.rejection_count || 0}/3 strikes)
                          </>
                        ) : (
                          <>
                            {photographer.rejection_count || 0}/3 strikes
                          </>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        {/* Message Button */}
                        <button
                          onClick={() => {
                            setMessageRecipient(photographer);
                            setShowMessageModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Send message"
                        >
                          <MessageCircle size={14} />
                          Message
                        </button>
                        
                        {/* Block/Unblock Button */}
                        {photographer.is_blocked ? (
                          <button
                            onClick={() => handleUnblockPhotographer(photographer.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Unblock photographer"
                          >
                            <ShieldCheck size={14} />
                            Unblock
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlockPhotographer(photographer.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Block photographer"
                          >
                            <Ban size={14} />
                            Block
                          </button>
                        )}
                        
                        {/* Convert to Attendee Button */}
                        <button
                          onClick={() => handleConvertToAttendee(photographer.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Convert to attendee"
                        >
                          ⬇️ Attendee
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {users.filter(p => p.name.toLowerCase().includes(photographerSearch.toLowerCase())).length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                No photographers found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manage Attendees Modal */}
      {showAttendeesModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#111827' }}>Manage Attendees</h2>
              <button
                onClick={() => {
                  setShowAttendeesModal(false);
                  setAttendeeSearch('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#92400e', fontWeight: '600' }}>
                  <Clock size={14} style={{display: 'inline', marginRight: '4px'}} /> Pending Photographer Requests ({pendingRequests.length})
                </h3>
                {pendingRequests.map(request => (
                  <div
                    key={request.id}
                    style={{
                      padding: '12px',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{request.name}</div>
                      {request.email && (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{request.email}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApprovePhotographer(request.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Deny this photographer request?')) {
                            // Just refresh to remove from pending list
                            fetchEventAndPhotos();
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        ✗ Deny
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Broadcast to Attendees Button */}
            <button
              onClick={() => {
                setMessageRecipient({ role: 'attendee' });
                setShowMessageModal(true);
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={16} />
              Broadcast to All Attendees
            </button>

            <input
              type="text"
              placeholder="Search attendees..."
              value={attendeeSearch}
              onChange={(e) => setAttendeeSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px'
              }}
            />

            <div style={{ marginBottom: '12px', color: '#6b7280', fontSize: '14px' }}>
              {users.filter(u => u.role === 'attendee').length} attendee(s)
            </div>

            <div>
              {users
                .filter(u => u.role === 'attendee' && u.name.toLowerCase().includes(attendeeSearch.toLowerCase()))
                .map(attendee => (
                  <div
                    key={attendee.id}
                    style={{
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'white'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        {attendee.name}
                      </div>
                      {attendee.email && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {attendee.email}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>
                        👤 Attendee
                      </div>
                      
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          onClick={() => {
                            setMessageRecipient(attendee);
                            setShowMessageModal(true);
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Send message"
                        >
                          <MessageCircle size={14} />
                          Message
                        </button>
                        
                        {/* Convert to Photographer Button */}
                        <button
                          onClick={() => handleConvertToPhotographer(attendee.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#3B82F6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Convert to photographer"
                        >
                          ⬆️ Photographer
                        </button>
                        
                        {/* Send Full Gallery Button */}
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Send full gallery link to ${attendee.name}?`)) {
                              return;
                            }
                            try {
                              const response = await eventsAPI.sendFullGallery(eventId, [attendee.id]);
                              const success = response.data.results?.[0]?.status === 'sent';
                              if (success) {
                                alert(`Successfully sent gallery link to ${attendee.name}!`);
                              } else {
                                alert(`Failed to send gallery link to ${attendee.name}`);
                              }
                            } catch (err) {
                              console.error('Failed to send gallery link:', err);
                              alert(`Failed to send gallery link: ${err.response?.data?.error || err.message}`);
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Send full gallery link"
                        >
                          <Share2 size={14} />
                          Send Gallery
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {users.filter(u => u.role === 'attendee' && u.name.toLowerCase().includes(attendeeSearch.toLowerCase())).length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                No attendees found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>
                {messageRecipient?.name ? `Message ${messageRecipient.name}` : 
                 messageRecipient?.role === 'attendee' ? 'Broadcast to All Attendees' :
                 messageRecipient?.role === 'photographer' ? 'Broadcast to All Photographers' :
                 'Broadcast Message'}
              </h3>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageText('');
                  setMessageRecipient(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {!messageRecipient?.name && (
              <div style={{
                padding: '12px',
                background: messageRecipient?.role === 'attendee' ? '#d1fae5' : '#eff6ff',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                color: messageRecipient?.role === 'attendee' ? '#065f46' : '#1e40af',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Send size={16} />
                {messageRecipient?.role === 'attendee' 
                  ? `This message will be sent to all ${users.filter(u => u.role === 'attendee').length} attendees`
                  : messageRecipient?.role === 'photographer'
                  ? `This message will be sent to all ${users.filter(u => u.role === 'photographer').length} photographers`
                  : `This message will be sent to all ${users.length} users`}
              </div>
            )}

            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '16px'
              }}
            />

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageText('');
                  setMessageRecipient(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageText.trim()}
                style={{
                  padding: '10px 20px',
                  background: sendingMessage || !messageText.trim() ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: sendingMessage || !messageText.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Send size={14} />
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slideshow Overlay */}
      {slideshowActive && (
        <div className="slideshow-overlay" onClick={stopSlideshow}>
          <img
            key={currentSlideIndex}
            src={activeSlideshowPhotos[currentSlideIndex]?.url}
            alt="Slideshow"
            className="slideshow-image"
            style={{
              animation: 'kenBurnsFade 5s ease-out'
            }}
          />
          <style>{`
            @keyframes kenBurnsFade {
              0% {
                opacity: 0;
                transform: scale(1);
              }
              5% {
                opacity: 1;
              }
              100% {
                opacity: 1;
                transform: scale(1.15);
              }
            }
          `}</style>
          <button className="slideshow-close">
            Close (ESC)
          </button>
          <div className="slideshow-counter">
            {currentSlideIndex + 1} / {activeSlideshowPhotos.length}
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowQRModal(false)}
        >
          <div 
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowQRModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '8px',
              color: theme === 'dark' ? 'white' : '#1f2937',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Event QR Code
            </h2>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              marginBottom: '32px',
              textAlign: 'center',
              fontSize: '16px'
            }}>
              {event?.name}
            </p>
            
            {event?.qr_code && (
              <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <img
                  src={event.qr_code}
                  alt="Event QR Code"
                  style={{
                    width: '100%',
                    maxWidth: '320px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              textAlign: 'center',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0
            }}>
              Photographers can scan this QR code to join the event and upload photos
            </p>
          </div>
        </div>
      )}

      {/* QR Code Selection Modal */}
      {showQRSelectionModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowQRSelectionModal(false)}
        >
          <div 
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowQRSelectionModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '24px',
              color: theme === 'dark' ? 'white' : '#1f2937',
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Select QR Code
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowQRSelectionModal(false);
                  setShowQRModal(true);
                }}
                style={{
                  padding: '16px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Camera size={20} />
                Photographer QR Code
              </button>
              
              <button
                onClick={() => {
                  setShowQRSelectionModal(false);
                  setShowRegistrationQR(true);
                }}
                style={{
                  padding: '16px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <User size={20} />
                Registration QR Code
              </button>
              
              <button
                onClick={() => {
                  setShowQRSelectionModal(false);
                  setShowCameraQR(true);
                }}
                style={{
                  padding: '16px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Camera size={20} />
                Pro Camera QR (FTP)
              </button>
              
              <button
                onClick={() => {
                  setShowQRSelectionModal(false);
                  setShowWebCameraQR(true);
                }}
                style={{
                  padding: '16px',
                  backgroundColor: '#EC4899',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Camera size={20} />
                Web Camera QR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration QR Code Modal */}
      {showRegistrationQR && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowRegistrationQR(false)}
        >
          <div 
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowRegistrationQR(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '8px',
              color: theme === 'dark' ? 'white' : '#1f2937',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              📝 Registration QR Code
            </h2>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              marginBottom: '32px',
              textAlign: 'center',
              fontSize: '16px'
            }}>
              {event?.name}
            </p>
            
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <QRCodeSVG
                value={`${window.location.origin}/register/${eventId}`}
                size={320}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              textAlign: 'center',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0
            }}>
              Attendees can scan this QR code to register and receive their photos via text message
            </p>
          </div>
        </div>
      )}

      {/* Web Camera QR Code Modal */}
      {showWebCameraQR && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowWebCameraQR(false)}
        >
          <div 
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowWebCameraQR(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '8px',
              color: theme === 'dark' ? 'white' : '#1f2937',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              📱 Web Camera QR
            </h2>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              marginBottom: '32px',
              textAlign: 'center',
              fontSize: '16px'
            }}>
              {event?.name}
            </p>
            
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <QRCodeSVG
                value={`${window.location.origin}/register/${eventId}?openCamera=true&role=photographer`}
                size={320}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              textAlign: 'center',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0
            }}>
              Scan with phone camera to register and start taking photos instantly via web browser
            </p>
          </div>
        </div>
      )}

      {/* Camera QR Code Modal */}
      {showCameraQR && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowCameraQR(false)}
        >
          <div 
            style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '20px',
              padding: '40px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCameraQR(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            
            <h2 style={{ 
              marginTop: 0, 
              marginBottom: '8px',
              color: theme === 'dark' ? 'white' : '#1f2937',
              fontSize: '28px',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              📷 Camera Registration QR
            </h2>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              marginBottom: '32px',
              textAlign: 'center',
              fontSize: '16px'
            }}>
              {event?.name}
            </p>
            
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <QRCodeSVG
                value={`${window.location.origin}/camera-register/${eventId}`}
                size={320}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <p style={{ 
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              textAlign: 'center',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0
            }}>
              Camera photographers scan this QR code to get FTP credentials for automatic photo uploads
            </p>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditEventModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
            padding: '32px',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: theme === 'dark' ? '#f9fafb' : '#1f2937' }}>Edit Event</h2>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              
              if (!editEventName || !editStartTime || !editEndTime) {
                alert('Please fill in all fields');
                return;
              }
              
              if (new Date(editStartTime) >= new Date(editEndTime)) {
                alert('Start time must be before end time');
                return;
              }
              
              try {
                setSavingEvent(true);
                
                // Upload watermark if provided
                let watermarkUrl = event?.watermark_url; // Keep existing watermark by default
                if (watermarkFile) {
                  const formData = new FormData();
                  formData.append('watermark', watermarkFile);
                  try {
                    const uploadResponse = await eventsAPI.uploadWatermark(formData);
                    watermarkUrl = uploadResponse.data.url;
                  } catch (uploadError) {
                    console.error('Error uploading watermark:', uploadError);
                    alert('Failed to upload watermark. Updating event without changing watermark.');
                  }
                }
                
                console.log('📤 Updating event:', {
                  eventId,
                  name: editEventName,
                  start_time: new Date(editStartTime).getTime(),
                  end_time: new Date(editEndTime).getTime(),
                  status: event?.status || 'active',
                  watermark_url: watermarkUrl
                });
                
                const response = await eventsAPI.update(eventId, {
                  name: editEventName,
                  start_time: new Date(editStartTime).getTime(),
                  end_time: new Date(editEndTime).getTime(),
                  status: event?.status || 'active',
                  watermark_url: watermarkUrl
                });
                
                console.log('✅ Event update response:', response);
                
                // Update local event state without refetching to avoid form reset
                setEvent(prev => ({
                  ...prev,
                  name: editEventName,
                  start_time: new Date(editStartTime).getTime().toString(),
                  end_time: new Date(editEndTime).getTime().toString(),
                  watermark_url: watermarkUrl
                }));
                
                setShowEditEventModal(false);
                alert('Event updated successfully!');
              } catch (err) {
                console.error('Failed to update event:', err);
                alert(`Failed to update event: ${err.response?.data?.error || err.message}`);
              } finally {
                setSavingEvent(false);
              }
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: theme === 'dark' ? '#f9fafb' : '#374151' }}>
                  Event Name
                </label>
                <input
                  type="text"
                  value={editEventName}
                  onChange={(e) => setEditEventName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
                    backgroundColor: theme === 'dark' ? '#374151' : 'white',
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: theme === 'dark' ? '#f9fafb' : '#374151' }}>
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  value={editStartTime}
                  onChange={(e) => setEditStartTime(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
                    backgroundColor: theme === 'dark' ? '#374151' : 'white',
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: theme === 'dark' ? '#f9fafb' : '#374151' }}>
                  End Time
                </label>
                <input
                  type="datetime-local"
                  value={editEndTime}
                  onChange={(e) => setEditEndTime(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
                    backgroundColor: theme === 'dark' ? '#374151' : 'white',
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: theme === 'dark' ? '#f9fafb' : '#374151' }}>
                  Event Watermark (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setWatermarkFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setWatermarkPreview(reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
                    backgroundColor: theme === 'dark' ? '#374151' : 'white',
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937'
                  }}
                />
                {(watermarkPreview || event?.watermark_url) && (
                  <div style={{ marginTop: '12px' }}>
                    <img
                      src={watermarkPreview || event?.watermark_url}
                      alt="Watermark preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        border: `1px solid ${theme === 'dark' ? '#374151' : '#d1d5db'}`,
                        borderRadius: '8px',
                        padding: '8px',
                        backgroundColor: theme === 'dark' ? '#111827' : '#f9fafb'
                      }}
                    />
                  </div>
                )}
                <p style={{ fontSize: '12px', color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginTop: '8px' }}>
                  Upload a logo or watermark to be added to all event photos
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowEditEventModal(false)}
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                    color: theme === 'dark' ? '#f9fafb' : '#1f2937',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEvent}
                  style={{
                    padding: '10px 20px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    cursor: savingEvent ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: savingEvent ? 0.6 : 1
                  }}
                >
                  {savingEvent ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Send Gallery Modal */}
      {showSendGalleryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: theme === 'dark' ? '#1f2937' : 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', color: theme === 'dark' ? '#f9fafb' : '#111827' }}>
                Send Full Gallery Link
              </h3>
              <button
                onClick={() => setShowSendGalleryModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              padding: '12px',
              background: '#d1fae5',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#065f46'
            }}>
              Select attendees to send the full event gallery link via email
            </div>

            {/* Select All Checkbox */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              background: theme === 'dark' ? '#374151' : '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}>
              <input
                type="checkbox"
                checked={selectAllAttendees}
                onChange={(e) => {
                  setSelectAllAttendees(e.target.checked);
                  if (e.target.checked) {
                    setSelectedAttendees(galleryAttendees.map(a => a.id));
                  } else {
                    setSelectedAttendees([]);
                  }
                }}
                style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ color: theme === 'dark' ? '#f9fafb' : '#111827' }}>
                Select All ({galleryAttendees.length} attendees)
              </span>
            </label>

            {/* Attendee List */}
            <div style={{ marginBottom: '20px', maxHeight: '300px', overflow: 'auto' }}>
              {galleryAttendees.map(attendee => (
                <label key={attendee.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderBottom: `1px solid ${theme === 'dark' ? '#374151' : '#e5e7eb'}`,
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedAttendees.includes(attendee.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAttendees([...selectedAttendees, attendee.id]);
                      } else {
                        setSelectedAttendees(selectedAttendees.filter(id => id !== attendee.id));
                        setSelectAllAttendees(false);
                      }
                    }}
                    style={{ marginRight: '12px', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontWeight: '500', color: theme === 'dark' ? '#f9fafb' : '#111827' }}>
                      {attendee.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {attendee.email || 'No email'}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* Send Button */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSendGalleryModal(false)}
                style={{
                  padding: '10px 20px',
                  border: `1px solid ${theme === 'dark' ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '8px',
                  background: 'transparent',
                  color: theme === 'dark' ? '#f9fafb' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedAttendees.length === 0) {
                    alert('Please select at least one attendee');
                    return;
                  }

                  if (!window.confirm(`Send gallery link to ${selectedAttendees.length} attendee(s)?`)) {
                    return;
                  }

                  try {
                    setSendingFullGallery(true);
                    
                    // Send to selected attendees
                    const response = await eventsAPI.sendFullGallery(eventId, selectedAttendees);
                    
                    const successCount = response.data.results?.filter(r => r.status === 'sent').length || 0;
                    alert(`Successfully sent gallery link to ${successCount} of ${selectedAttendees.length} attendee(s)!`);
                    setShowSendGalleryModal(false);
                  } catch (err) {
                    console.error('Failed to send gallery links:', err);
                    alert(`Failed to send gallery links: ${err.response?.data?.error || err.message}`);
                  } finally {
                    setSendingFullGallery(false);
                  }
                }}
                disabled={sendingFullGallery || selectedAttendees.length === 0}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: selectedAttendees.length === 0 ? '#9ca3af' : '#10B981',
                  color: 'white',
                  cursor: selectedAttendees.length === 0 || sendingFullGallery ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {sendingFullGallery ? 'Sending...' : `Send to ${selectedAttendees.length} attendee(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Native Camera Upload Modal */}
      {showCameraCapture && (
        <NativeCameraUpload
          onUpload={handleCameraCapture}
          onClose={() => setShowCameraCapture(false)}
        />
      )}

      {/* Unified Upload Modal */}
      {showZipUpload && (
        <UnifiedUpload
          eventId={eventId}
          onClose={() => setShowZipUpload(false)}
          onUploadComplete={() => fetchEventAndPhotos()}
          theme={theme}
        />
      )}

      {/* OLD Zip Upload Modal - REPLACED */}
      {false && showZipUpload && (
        <div className="modal-overlay" onClick={() => !uploadingZip && setShowZipUpload(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: theme === 'dark' ? '#fff' : '#000' }}>Upload Photos from Zip</h2>
              {!uploadingZip && (
                <button onClick={() => setShowZipUpload(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={24} color={theme === 'dark' ? '#fff' : '#000'} />
                </button>
              )}
            </div>
            
            {!uploadingZip ? (
              <div>
                <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '20px' }}>
                  Select a zip file containing photos to upload to this event. All photos will be uploaded to B2 storage.
                </p>
                <input
                  type="file"
                  accept=".zip"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    if (!file.name.endsWith('.zip')) {
                      alert('Please select a zip file');
                      return;
                    }
                    
                    try {
                      setUploadingZip(true);
                      setZipUploadProgress({ current: 0, total: 0 });
                      
                      const formData = new FormData();
                      formData.append('zipFile', file);
                      
                      const response = await eventsAPI.uploadZipPhotos(eventId, formData, (progress) => {
                        setZipUploadProgress(progress);
                      });
                      
                      alert(`Successfully uploaded ${response.data.photosUploaded} photos!`);
                      setShowZipUpload(false);
                      
                      // Refresh photos
                      fetchEventAndPhotos();
                    } catch (err) {
                      console.error('Zip upload error:', err);
                      alert(`Failed to upload zip: ${err.response?.data?.error || err.message}`);
                    } finally {
                      setUploadingZip(false);
                      setZipUploadProgress({ current: 0, total: 0 });
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `2px dashed ${theme === 'dark' ? '#4b5563' : '#d1d5db'}`,
                    borderRadius: '8px',
                    background: theme === 'dark' ? '#1f2937' : '#f9fafb',
                    color: theme === 'dark' ? '#fff' : '#000',
                    cursor: 'pointer'
                  }}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    border: '4px solid #f3f4f6', 
                    borderTop: '4px solid #F97316',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto'
                  }} />
                </div>
                <p style={{ color: theme === 'dark' ? '#fff' : '#000', fontWeight: '500', marginBottom: '10px' }}>
                  Uploading Photos...
                </p>
                {zipUploadProgress.total > 0 && (
                  <p style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                    {(() => {
                      const formatBytes = (bytes) => {
                        if (bytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
                      };
                      const percent = Math.round((zipUploadProgress.current / zipUploadProgress.total) * 100);
                      return `${formatBytes(zipUploadProgress.current)} of ${formatBytes(zipUploadProgress.total)} (${percent}%)`;
                    })()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
    </div>
  );
}

export default EventPhotos;
